import type { RaRecord } from "ra-core";

import type {
  ContactConversationMessage,
  EchoflicksVideoSession,
} from "../../types";

/**
 * Read-time de-duplication for the live AWS dashboard.
 *
 * The dashboard read-model contains TWO rows for every echo/session/conversation
 * because the producers disagree on the session identity they emit:
 *   - the WhatsApp bot keys its events by `session_{user}_{ts}` (and inbound
 *     webhooks by `contact-{user}`),
 *   - the ML orchestrator and video composer key their events by `workload_id`.
 * The dashboard-indexer derives the primary key of SESSION#/ECHO# rows from that
 * session id, so one logical journey is materialised as two rows that the list
 * endpoints return verbatim. The bot also emits `final_video.created` in addition
 * to the composer, guaranteeing two ECHO rows.
 *
 * This collapses those duplicates at read time. It is a presentation-layer
 * band-aid; the durable fix canonicalises the identity in the indexer
 * (echoflicks-infra) + a backfill. Reliable join keys:
 *   - sessions: both rows carry the same `workload_id` field,
 *   - echoes:   both rows carry the same `final_video_storage_path`
 *               (the bot and composer send the same final-video S3 key).
 *
 * Opt out at build time with `VITE_ECHOFLICKS_DASHBOARD_DEDUPE=off`
 * (default: enabled).
 */

const DISABLED_VALUES = new Set(["off", "false", "0", "no", "disabled"]);

export function isDashboardDedupeEnabled(): boolean {
  const raw = String(import.meta.env?.VITE_ECHOFLICKS_DASHBOARD_DEDUPE ?? "")
    .trim()
    .toLowerCase();
  return !DISABLED_VALUES.has(raw);
}

function norm(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text === "" ? null : text;
}

function isEmpty(value: unknown): boolean {
  return value == null || value === "";
}

/**
 * Keep `winner` but backfill any field it is missing from `loser`, so a rating or
 * scene count that only landed on the duplicate row is not lost.
 */
function mergeBackfill<T extends RaRecord>(winner: T, loser: T): T {
  const out: Record<string, unknown> = { ...winner };
  for (const key of Object.keys(loser)) {
    if (isEmpty(out[key])) {
      const value = (loser as Record<string, unknown>)[key];
      if (!isEmpty(value)) out[key] = value;
    }
  }
  return out as T;
}

function dedupeRecords<T extends RaRecord>(
  records: T[],
  keyOf: (record: T) => string | null,
  better: (a: T, b: T) => T,
): T[] {
  const order: string[] = [];
  const byKey = new Map<string, T>();
  for (const record of records) {
    // No reliable join key -> never merge: bucket the row under its own id.
    const key = keyOf(record) ?? `__id:${String(record.id)}`;
    const existing = byKey.get(key);
    if (existing === undefined) {
      byKey.set(key, record);
      order.push(key);
      continue;
    }
    const winner = better(existing, record);
    const loser = winner === existing ? record : existing;
    byKey.set(key, mergeBackfill(winner, loser));
  }
  return order.map((key) => byKey.get(key) as T);
}

// --- Echoes / video sessions -------------------------------------------------

const SESSION_STATUS_RANK: Record<string, number> = {
  completed: 100,
  rendering: 60,
  composition_queued: 55,
  script_ready: 50,
  scripted: 50,
  in_progress: 40,
  waiting_for_user: 35,
  queued: 30,
  media_received: 20,
  active: 15,
  cancelled: 5,
  failed: 4,
};

export function echoSessionKey(record: RaRecord): string | null {
  const session = record as EchoflicksVideoSession;
  const workloadId = norm(session.workload_id);
  if (workloadId) return `wl:${workloadId}`;
  const finalVideo =
    norm(session.final_video_storage_path) ?? norm(session.final_video_s3_key);
  if (finalVideo) return `fv:${finalVideo}`;
  return null;
}

function sessionScore(record: RaRecord): number {
  const session = record as EchoflicksVideoSession;
  let score = 0;
  if (session.has_final_video) score += 1000;
  if (session.final_video_storage_path || session.final_video_s3_key) {
    score += 500;
  }
  score += SESSION_STATUS_RANK[String(session.status)] ?? 0;
  return score;
}

function betterSession(a: RaRecord, b: RaRecord): RaRecord {
  const scoreA = sessionScore(a);
  const scoreB = sessionScore(b);
  if (scoreA !== scoreB) return scoreA > scoreB ? a : b;
  const dateA = String(
    (a as EchoflicksVideoSession).display_date ??
      (a as EchoflicksVideoSession).completed_at ??
      "",
  );
  const dateB = String(
    (b as EchoflicksVideoSession).display_date ??
      (b as EchoflicksVideoSession).completed_at ??
      "",
  );
  if (dateA !== dateB) return dateA > dateB ? a : b;
  // Stable, deterministic tiebreak.
  return String(a.id) <= String(b.id) ? a : b;
}

export function collapseEchoSessions(records: RaRecord[]): RaRecord[] {
  if (!isDashboardDedupeEnabled() || records.length < 2) return records;
  return dedupeRecords(records, echoSessionKey, betterSession);
}

// --- Contact conversation ----------------------------------------------------

export function conversationKey(message: ContactConversationMessage): string {
  const providerMessageId = norm(message.provider_message_id);
  if (providerMessageId) return `pmid:${providerMessageId}`;
  const text = norm(message.text) ?? norm(message.body) ?? "";
  const normalizedText = text.replace(/\s+/g, " ").toLowerCase();
  return `f:${message.direction}|${normalizedText}|${message.occurred_at}`;
}

export function collapseConversation(
  messages: ContactConversationMessage[],
): ContactConversationMessage[] {
  if (!isDashboardDedupeEnabled() || messages.length < 2) return messages;
  // Preserve the existing chronological order; keep the first occurrence and
  // backfill missing fields (e.g. a later provider status) from duplicates.
  return dedupeRecords(messages, conversationKey, (a) => a);
}
